<template>
  <div class="border-surface-container-highest rounded-large @container container border p-4">
    <div class="flex flex-col gap-4 @4xl:flex-row">
      <div class="flex grow flex-col gap-2">
        <h3 class="text-body-large text-[18px] text-balance">
          <span v-if="memberFirstname" class="text-on-surface-variant">
            {{ memberFirstname }}
          </span>
          {{ indikator.text }}
        </h3>
        <div
          v-if="selfLabel && !frozen"
          class="text-body-medium text-on-secondary-container bg-secondary-container rounded-small flex w-min flex-row px-2 py-1 whitespace-nowrap"
        >
          <span> Selbst: {{ selfLabel }} </span>
        </div>
      </div>
      <div class="flex shrink-0 flex-row gap-2 @4xl:justify-end">
        <BaseButtonGroup v-if="!frozen">
          <BaseTextButton
            v-for="item in teacherScale.items"
            :key="item.value"
            size="S"
            color="filled"
            shape="round"
            class="whitespace-normal!"
            :toggle="true"
            :selected="teacherValue === item.value"
            @click="selectValue(item.value)"
          >
            {{ item.label }}
          </BaseTextButton>
          <BaseIconButton
            v-tooltip="$t('lumia.lesson.teacherAssessment.notObserved')"
            size="S"
            color="filled"
            shape="round"
            icon="visibility_off"
            :toggle="true"
            :selected="teacherValue === null"
            @click="selectNotObserved"
          />
        </BaseButtonGroup>
        <div v-else class="flex flex-row items-center gap-3">
          <PerspectiveValueBarNew
            class="min-w-90"
            :values="barValues"
            :scale="teacherScale.items"
            :all-perspectives="[SELF_PERSPECTIVE, TEACHER_PERSPECTIVE]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import type { LessonScale, TeacherAssessmentIndikator } from '../models/teacher-assessment'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import PerspectiveValueBarNew from '@/core/components/analytics/question-list/PerspectiveValueBarNew.vue'
  import { SELF_PERSPECTIVE, type Perspective } from '@/core/models/perspective.ts'
  import { TEACHER_PERSPECTIVE } from '../models/kompetenzprofil.ts'

  interface Props {
    indikator: TeacherAssessmentIndikator
    teacherScale: LessonScale
    selfScale: LessonScale | null
    teacherValue: number | null | undefined
    selfValue: number | null | undefined
    frozen: boolean
    memberFirstname?: string
  }

  const props = defineProps<Props>()

  const selfLabel = computed(() => {
    if (props.selfValue == null || !props.selfScale) return ''
    return props.selfScale.items.find((item) => item.value === props.selfValue)?.label ?? ''
  })

  const barValues = computed<
    { perspective: Perspective; average: number | null; numberOfFeedbacks: number }[]
  >(() => {
    const values = []
    if (props.teacherValue !== null && props.teacherValue !== undefined) {
      values.push({
        perspective: TEACHER_PERSPECTIVE,
        average: props.teacherValue,
        numberOfFeedbacks: 0,
      })
    }
    if (props.selfValue !== null && props.selfValue !== undefined) {
      values.push({ perspective: SELF_PERSPECTIVE, average: props.selfValue, numberOfFeedbacks: 1 })
    }
    return values
  })

  const emit = defineEmits<{
    'update:teacherValue': [value: number | null]
  }>()

  const selectValue = (value: number) => {
    if (props.frozen || props.teacherValue === value) {
      return
    }
    emit('update:teacherValue', value)
  }

  const selectNotObserved = () => {
    if (props.frozen || props.teacherValue === null) {
      return
    }
    emit('update:teacherValue', null)
  }
</script>
